import { FocusEvent, useMemo } from 'react'

import * as StyledSelectItem from './SelectBoxItem.styled'
import { isDefaultItem } from './selectHelper'
import { FormatValue, RenderItem, SelectItemType, SelectValueType } from './types'


const defaultRenderItemFn: RenderItem<SelectItemType> = (option: SelectItemType) => (
  <>
    {option.label}
    {option.note && <StyledSelectItem.Note id={'note'}>{option.note}</StyledSelectItem.Note>}
  </>
)

export interface SelectItemsChangeOptions {
  forceClose?: boolean
}

export interface SelectBoxItemProps<TValue extends SelectValueType, TOption> {
  dataItemId: string
  className?: string
  item: TOption
  formatValue: FormatValue<TOption, TValue>
  renderItem: RenderItem<TOption>
  onChange: (item: TOption, options?: SelectItemsChangeOptions) => void
  onFocus?: (e: FocusEvent<HTMLLIElement>) => void
  testId?: string
  isFocused: boolean
}

export function SelectBoxItem<TValue extends SelectValueType, TOption>({
  dataItemId,
  item,
  className,
  renderItem,
  formatValue,
  onChange,
  onFocus,
  testId,
  isFocused
}: SelectBoxItemProps<TValue, TOption>) {
  const handleClick = (forceClose?: boolean) => {
    onChange(item, { forceClose })
  }

  const itemRender = useMemo(() => {
    if (renderItem) {
      return renderItem(item)
    }

    if (isDefaultItem(item)) {
      return defaultRenderItemFn(item)
    }

    throw new Error('Missing render item function')
  }, [item])

  return (
    <StyledSelectItem.ItemContainer
      role='option'
      data-testid={testId ?? 'item'}
      tabIndex={-1}
      onFocus={(e) => {
        if (onFocus) {
          onFocus(e)
        }
      }}
      aria-selected={isFocused}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleClick(true)
        }
      }}
      data-select-item={
        dataItemId + '__' + formatValue(typeof item === 'string' ? (item.replace(/'/, '%27') as TOption) : item)
      }
      className={className}
      onClick={() => handleClick(false)}
    >
      {itemRender}
    </StyledSelectItem.ItemContainer>
  )
}
